import { BrochureResponseDto, brochureApi } from 'apiConnector/document/brochure';
import { BlobWithFilename, DocumentError } from 'apiConnector/document/document';
import { useStreamingContext } from 'contexts/streaming.context';
import { DocumentItem } from 'epic/common-form/Documentation';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EMPTY, first, firstValueFrom, map, mergeMap, Observable, throwError } from 'rxjs';
import { blotterPanelDownloadStatusUpdated } from 'state/blotters/blotterPanel/blotterPanel.action';
import { downloadFile } from 'utils/documents/downloadDocument';
import { DocumentationEvent } from 'utils/streaming/model';

type GenerateDocumentArgs<GenerateReponse> = {
  isMessageRelevant: (ev: DocumentationEvent) => boolean;
  documentationMessage$: Observable<DocumentationEvent>;
  generateDocument: () => Observable<GenerateReponse>;
  isGenerateSuccessful: (r: GenerateReponse) => boolean;
  retrieveDoc: (r: GenerateReponse) => Observable<BlobWithFilename>;
  toDocumentError: (r: GenerateReponse) => DocumentError;
};

function waitAndDownload<GenerateReponse>({
  isGenerateSuccessful,
  isMessageRelevant,
  generateDocument,
  retrieveDoc,
  documentationMessage$,
  toDocumentError,
}: GenerateDocumentArgs<GenerateReponse>): Promise<BlobWithFilename> {
  return firstValueFrom(
    generateDocument().pipe(
      mergeMap(response => {
        if (isGenerateSuccessful(response)) {
          return documentationMessage$.pipe(
            first(isMessageRelevant),
            mergeMap(event => {
              switch (event.type) {
                case 'NextDocumentation':
                  return retrieveDoc(response);
                case 'NextDocumentationError':
                  return throwError(
                    () => ({ type: 'END_USER_ERROR', errorMessages: event.messages ?? [] }) satisfies DocumentError,
                  );
                default:
                  return throwError(() => ({ type: 'TECHNICAL' }) satisfies DocumentError) as never;
              }
            }),
          );
        } else {
          const documentError = toDocumentError(response);
          return throwError(() => documentError);
        }
      }),
    ),
  );
}

function mapBrochureResponse(response: { payload: Blob; attachmentName?: string }): BlobWithFilename {
  return {
    blob: response.payload,
    filename: response.attachmentName,
  };
}

function useGenerateBrochureDocument(quoteId?: string): () => Promise<BlobWithFilename> {
  const { documentationMessage$ } = useStreamingContext();

  const startDocumentGeneration = useCallback(() => brochureApi.generateBrochure({ quoteId: quoteId! }), [quoteId]);

  const isMessageRelevant = useCallback(
    () => (m: DocumentationEvent) => m.quoteId === quoteId && (m.docType as string) === 'Brochure',
    [quoteId],
  );

  const retrieveDoc = useCallback(
    (response: BrochureResponseDto): Observable<BlobWithFilename> =>
      response.isSuccess ? brochureApi.downloadBrochure(response.id).pipe(map(mapBrochureResponse)) : EMPTY,
    [],
  );

  const generateDocument = useCallback(
    () =>
      waitAndDownload<BrochureResponseDto>({
        documentationMessage$,
        generateDocument: () => startDocumentGeneration(),
        isGenerateSuccessful: (response: BrochureResponseDto) => response.isSuccess,
        isMessageRelevant: isMessageRelevant(),
        retrieveDoc,
        toDocumentError: r =>
          r.isSuccess === false
            ? { type: 'END_USER_ERROR', errorMessages: r.errors }
            : ({ type: 'TECHNICAL' } satisfies DocumentError),
      }),
    [startDocumentGeneration, documentationMessage$, isMessageRelevant, retrieveDoc],
  );

  return generateDocument;
}

type DocumentDownload = {
  isDownloading: boolean;
  download: (doc: DocumentItem) => void;
  error: DocumentError | undefined;
};

export function useDownloadBrochure(quoteId: string): DocumentDownload {
  const generateBrochure = useGenerateBrochureDocument(quoteId);
  const dispatch = useDispatch();
  const [isDownloadingBrochure, setIsDownloadingBrochure] = useState(false);
  const [error, setError] = useState<DocumentError | undefined>(undefined);
  const downloadBrochure = useCallback(
    async (doc: DocumentItem) => {
      setIsDownloadingBrochure(true);
      setError(undefined);
      try {
        dispatch(blotterPanelDownloadStatusUpdated('Brochure', doc.language, doc.format, 'Pending'));
        const { blob, filename } = await generateBrochure();
        downloadFile(blob, filename ?? `Brochure_${quoteId}.pdf`);
        dispatch(blotterPanelDownloadStatusUpdated('Brochure', doc.language, doc.format, 'Validated'));
      } catch (error) {
        const documentError = error as DocumentError;
        setError(documentError);
        dispatch(blotterPanelDownloadStatusUpdated('Brochure', doc.language, doc.format, 'Generation failed'));
      } finally {
        setIsDownloadingBrochure(false);
      }
    },
    [generateBrochure, quoteId, dispatch],
  );

  return {
    isDownloading: isDownloadingBrochure,
    download: downloadBrochure,
    error,
  };
}
